import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Calendar, MapPin, Plus, Users, Sparkles, 
  Monitor, MoreHorizontal, CheckCircle, Edit, Trash2, 
  CalendarDays, Clock
} from 'lucide-react';

const EVENT_TYPES = [
  { value: 'card_show', label: 'Card Show', icon: Sparkles },
  { value: 'meetup', label: 'Meetup', icon: Users },
  { value: 'trade_night', label: 'Trade Night', icon: CalendarDays },
  { value: 'online', label: 'Online Event', icon: Monitor },
  { value: 'other', label: 'Other', icon: MoreHorizontal },
];

const getEventTypeInfo = (type) => {
  return EVENT_TYPES.find(t => t.value === type) || EVENT_TYPES[4];
};

const formatEventDate = (dateString) => {
  const date = new Date(dateString);
  const options = { 
    weekday: 'short', 
    month: 'short', 
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  };
  return date.toLocaleDateString('en-US', options);
};

const getRelativeTime = (dateString) => {
  const now = new Date();
  const eventDate = new Date(dateString);
  const diffMs = eventDate - now;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) {
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    if (diffHours <= 0) return 'Starting soon';
    return `In ${diffHours}h`;
  } else if (diffDays === 1) {
    return 'Tomorrow';
  } else if (diffDays < 7) {
    return `In ${diffDays} days`;
  } else if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return `In ${weeks} week${weeks > 1 ? 's' : ''}`;
  } else {
    const months = Math.floor(diffDays / 30);
    return `In ${months} month${months > 1 ? 's' : ''}`;
  }
};

const EventCard = ({ event, onEdit, onDelete, isOwner }) => {
  const typeInfo = getEventTypeInfo(event.event_type);
  const TypeIcon = typeInfo.icon;
  
  return (
    <div 
      className="bg-zinc-900/50 border border-white/5 rounded-xl p-5 hover:border-white/10 transition-all group"
      data-testid={`event-card-${event.id}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          {/* Event Type Badge & Date */}
          <div className="flex items-center gap-2 mb-3">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-500/10 text-blue-400 border border-blue-500/20">
              <TypeIcon className="w-3 h-3" />
              {typeInfo.label}
            </span>
            {event.is_verified && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-500/10 text-green-400 border border-green-500/20">
                <CheckCircle className="w-3 h-3" />
                Verified
              </span>
            )}
          </div>
          
          {/* Title */}
          <h3 className="text-lg font-semibold text-white mb-2 truncate">
            {event.title}
          </h3>
          
          {/* Description */}
          {event.description && (
            <p className="text-sm text-zinc-400 mb-3 line-clamp-2">
              {event.description}
            </p>
          )}
          
          {/* Meta Info */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-zinc-500">
            <div className="flex items-center gap-1.5">
              <Calendar className="w-4 h-4" />
              <span>{formatEventDate(event.event_date)}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <MapPin className="w-4 h-4" />
              <span className="truncate max-w-[200px]">{event.location_name}</span>
            </div>
          </div>
          
          {/* Organizer */}
          <div className="mt-3 text-xs text-zinc-600">
            Posted by @{event.organizer_username}
          </div>
        </div>
        
        {/* Right Side - Time Badge & Actions */}
        <div className="flex flex-col items-end gap-2 shrink-0">
          <div className="px-3 py-1.5 rounded-lg bg-amber-500/10 border border-amber-500/20">
            <div className="flex items-center gap-1.5 text-amber-400">
              <Clock className="w-3.5 h-3.5" />
              <span className="text-xs font-medium">{getRelativeTime(event.event_date)}</span>
            </div>
          </div>
          
          {isOwner && (
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-zinc-400 hover:text-white"
                onClick={() => onEdit(event)}
                data-testid={`edit-event-${event.id}`}
              >
                <Edit className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-zinc-400 hover:text-red-400"
                onClick={() => onDelete(event.id)}
                data-testid={`delete-event-${event.id}`}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const EventForm = ({ event, onSubmit, onCancel, isLoading }) => {
  const [formData, setFormData] = useState({
    title: event?.title || '',
    description: event?.description || '',
    event_type: event?.event_type || 'meetup',
    event_date: event?.event_date ? event.event_date.slice(0, 16) : '',
    location_name: event?.location_name || '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast.error('Please enter an event title');
      return;
    }
    if (!formData.event_date) {
      toast.error('Please select an event date');
      return;
    }
    if (!formData.location_name.trim()) {
      toast.error('Please enter a location');
      return;
    }
    
    // Convert local datetime to ISO string
    const eventDateTime = new Date(formData.event_date).toISOString();
    
    onSubmit({
      ...formData,
      event_date: eventDateTime,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label htmlFor="title">Event Title *</Label>
        <Input
          id="title"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          placeholder="e.g., Phoenix Card Show 2025"
          className="mt-1.5 bg-zinc-800 border-zinc-700"
          data-testid="event-title-input"
        />
      </div>
      
      <div>
        <Label htmlFor="event_type">Event Type</Label>
        <Select
          value={formData.event_type}
          onValueChange={(value) => setFormData({ ...formData, event_type: value })}
        >
          <SelectTrigger className="mt-1.5 bg-zinc-800 border-zinc-700" data-testid="event-type-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-zinc-800 border-zinc-700">
            {EVENT_TYPES.map((type) => (
              <SelectItem key={type.value} value={type.value}>
                <div className="flex items-center gap-2">
                  <type.icon className="w-4 h-4" />
                  {type.label}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      <div>
        <Label htmlFor="event_date">Date & Time *</Label>
        <Input
          id="event_date"
          type="datetime-local"
          value={formData.event_date}
          onChange={(e) => setFormData({ ...formData, event_date: e.target.value })}
          className="mt-1.5 bg-zinc-800 border-zinc-700"
          min={new Date().toISOString().slice(0, 16)}
          data-testid="event-date-input"
        />
      </div>
      
      <div>
        <Label htmlFor="location_name">Location *</Label>
        <Input
          id="location_name"
          value={formData.location_name}
          onChange={(e) => setFormData({ ...formData, location_name: e.target.value })}
          placeholder="e.g., Phoenix Convention Center, AZ"
          className="mt-1.5 bg-zinc-800 border-zinc-700"
          data-testid="event-location-input"
        />
      </div>
      
      <div>
        <Label htmlFor="description">Description (optional)</Label>
        <Textarea
          id="description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          placeholder="Add details about the event..."
          className="mt-1.5 bg-zinc-800 border-zinc-700 min-h-[80px]"
          data-testid="event-description-input"
        />
      </div>
      
      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={isLoading} data-testid="submit-event-btn">
          {isLoading ? 'Saving...' : (event ? 'Update Event' : 'Post Event')}
        </Button>
      </div>
    </form>
  );
};

export default function CommunityHub() {
  const { isAuthenticated, user, api } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [filterType, setFilterType] = useState('all');

  useEffect(() => {
    fetchEvents();
  }, [filterType]);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const params = filterType !== 'all' ? `?event_type=${filterType}` : '';
      const response = await api().get(`/events${params}`);
      setEvents(response.data.events || []);
    } catch (error) {
      console.error('Failed to fetch events:', error);
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEvent = async (data) => {
    if (!isAuthenticated) {
      toast.error('Please sign in to post an event');
      return;
    }
    
    setSubmitting(true);
    try {
      await api().post('/events', data);
      toast.success('Event posted successfully!');
      setIsDialogOpen(false);
      fetchEvents();
    } catch (error) {
      console.error('Failed to create event:', error);
      toast.error(error.response?.data?.detail || 'Failed to create event');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateEvent = async (data) => {
    if (!editingEvent) return;
    
    setSubmitting(true);
    try {
      await api().put(`/events/${editingEvent.id}`, data);
      toast.success('Event updated successfully!');
      setEditingEvent(null);
      setIsDialogOpen(false);
      fetchEvents();
    } catch (error) {
      console.error('Failed to update event:', error);
      toast.error(error.response?.data?.detail || 'Failed to update event');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!confirm('Are you sure you want to delete this event?')) return;
    
    try {
      await api().delete(`/events/${eventId}`);
      toast.success('Event deleted');
      fetchEvents();
    } catch (error) {
      console.error('Failed to delete event:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete event');
    }
  };

  const openEditDialog = (event) => {
    setEditingEvent(event);
    setIsDialogOpen(true);
  };

  const closeDialog = () => {
    setEditingEvent(null);
    setIsDialogOpen(false);
  };

  return (
    <div className="min-h-screen bg-[#09090B]">
      <Navbar />
      
      <main className="pt-24 pb-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white" data-testid="community-hub-title">
              Community Events
            </h1>
            <p className="text-zinc-400 mt-1">
              Discover card shows, meetups, and trade nights near you
            </p>
          </div>
          
          <Dialog open={isDialogOpen} onOpenChange={(open) => {
            if (!open) closeDialog();
            else setIsDialogOpen(true);
          }}>
            <DialogTrigger asChild>
              <Button 
                className="bg-blue-600 hover:bg-blue-500"
                disabled={!isAuthenticated}
                data-testid="post-event-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Post Event
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
              <DialogHeader>
                <DialogTitle>{editingEvent ? 'Edit Event' : 'Post New Event'}</DialogTitle>
                <DialogDescription>
                  Share a card show, meetup, or trade night with the community.
                </DialogDescription>
              </DialogHeader>
              <EventForm
                event={editingEvent}
                onSubmit={editingEvent ? handleUpdateEvent : handleCreateEvent}
                onCancel={closeDialog}
                isLoading={submitting}
              />
            </DialogContent>
          </Dialog>
        </div>
        
        {/* Filter Tabs */}
        <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2">
          <Button
            variant={filterType === 'all' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setFilterType('all')}
            className={filterType === 'all' ? 'bg-blue-600' : ''}
            data-testid="filter-all"
          >
            All Events
          </Button>
          {EVENT_TYPES.map((type) => (
            <Button
              key={type.value}
              variant={filterType === type.value ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setFilterType(type.value)}
              className={filterType === type.value ? 'bg-blue-600' : ''}
              data-testid={`filter-${type.value}`}
            >
              <type.icon className="w-4 h-4 mr-1.5" />
              {type.label}
            </Button>
          ))}
        </div>
        
        {/* Events Feed */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-zinc-900/50 border border-white/5 rounded-xl p-5 animate-pulse">
                <div className="h-5 w-24 bg-zinc-800 rounded mb-3" />
                <div className="h-6 w-3/4 bg-zinc-800 rounded mb-2" />
                <div className="h-4 w-1/2 bg-zinc-800 rounded" />
              </div>
            ))}
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-16 bg-zinc-900/30 border border-white/5 rounded-xl">
            <CalendarDays className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-zinc-400 mb-2">No Upcoming Events</h3>
            <p className="text-zinc-500 mb-6">
              {filterType !== 'all' 
                ? `No ${getEventTypeInfo(filterType).label.toLowerCase()} events scheduled yet.`
                : 'Be the first to post an event for the community!'}
            </p>
            {isAuthenticated && (
              <Button onClick={() => setIsDialogOpen(true)} className="bg-blue-600 hover:bg-blue-500">
                <Plus className="w-4 h-4 mr-2" />
                Post Event
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-4" data-testid="events-feed">
            {events.map((event) => (
              <EventCard
                key={event.id}
                event={event}
                onEdit={openEditDialog}
                onDelete={handleDeleteEvent}
                isOwner={user?.id === event.organizer_id}
              />
            ))}
          </div>
        )}
        
        {/* Sign-in prompt for non-authenticated users */}
        {!isAuthenticated && (
          <div className="mt-8 p-4 bg-zinc-900/50 border border-white/5 rounded-xl text-center">
            <p className="text-zinc-400">
              <a href="/login" className="text-blue-400 hover:underline">Sign in</a> to post your own events
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
